import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

type StepperModalContextValue<T> = {
  values: T
  setValues: (values: Partial<T>) => void
  resetState: () => void
}

export type StepperModalValues = Record<string, unknown>

const StepperModalContext = createContext<
  StepperModalContextValue<unknown> | undefined
>(undefined)

export function useStepperModalContext<T>() {
  const context = useContext(StepperModalContext)
  if (!context) {
    throw new Error(
      "StepperModalContext used outside of StepperModalProvider component",
    )
  }

  return context as StepperModalContextValue<T>
}

export default function StepperModalProvider({
  initialValues,
  children,
}: {
  initialValues?: StepperModalValues | undefined
  children: ReactNode
}) {
  const [values, setValues] = useState<StepperModalValues | undefined>(
    initialValues,
  )

  const handleSetValues = useCallback(
    (newValues: Partial<StepperModalValues>) => {
      setValues((prevValues) => ({
        ...prevValues,
        ...newValues,
      }))
    },
    [],
  )

  const resetState = useCallback(() => {
    setValues(initialValues)
  }, [initialValues])

  const value = useMemo(
    () => ({
      values,
      setValues: handleSetValues,
      resetState,
    }),
    [handleSetValues, resetState, values],
  )

  return (
    <StepperModalContext.Provider value={value}>
      {children}
    </StepperModalContext.Provider>
  )
}
